import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, Home as HomeIcon, MapPin, Plus } from "lucide-react";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import { ErrorState } from "@shared/ui/feedback/ErrorState";
import { EmptyState } from "@shared/ui/feedback/EmptyState";
import { Card } from "@shared/ui/containers/Card";
import { Button } from "@shared/ui/primitives/Button";
import { Fab } from "@shared/ui/primitives/Fab";
import { formatDate } from "@shared/lib/format";
import { cn } from "@shared/lib/cn";
import { pageCopy } from "@shared/lib/pageCopy";
import { toast } from "@shared/ui/feedback/Toast";
import { HeroHeader } from "@shared/ui/headers/HeroHeader";
import {
  approvedPropertiesFromClaims,
  cancelPropertyClaim,
  fetchMyClaims,
  type PropertyClaim,
} from "./api";
import { ClaimPropertySheet } from "./ClaimPropertySheet";

const STATUS_TONE: Record<string, string> = {
  pending: "bg-warning/10 text-warning",
  approved: "bg-success/10 text-success",
  rejected: "bg-danger/10 text-danger",
  cancelled: "bg-ink-muted/10 text-ink-muted",
};

const RESIDENCY_LABEL: Record<string, string> = {
  resident: "Resident",
  non_resident: "Non-resident",
  temporary_resident: "Temporary",
};

/**
 * My properties — estate header, approved links, pending claims, and a
 * bottom-sheet flow to claim another property.
 */
export function MyProperties(): JSX.Element {
  const copy = pageCopy().properties;
  const queryClient = useQueryClient();
  const [claimOpen, setClaimOpen] = useState(false);

  const claimsQuery = useQuery({
    queryKey: ["my-property-claims"],
    queryFn: fetchMyClaims,
  });

  const cancelMutation = useMutation({
    mutationFn: cancelPropertyClaim,
    onSuccess: () => {
      toast.success("Claim withdrawn");
      void queryClient.invalidateQueries({ queryKey: ["my-property-claims"] });
    },
    onError: (e) =>
      toast.danger(
        "Couldn't cancel",
        e instanceof Error ? e.message : undefined,
      ),
  });

  const claims = claimsQuery.data ?? [];
  const approved = approvedPropertiesFromClaims(claims);
  const pending = claims.filter((c) => c.status === "pending");
  const rejected = claims.filter((c) => c.status === "rejected");

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
      />

      <div className="px-5 pt-5 pb-[calc(8rem+env(safe-area-inset-bottom,0px))]">
        {claimsQuery.isLoading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-28 rounded-2xl" />
            <Skeleton className="h-20 rounded-2xl" />
          </div>
        ) : claimsQuery.isError ? (
          <ErrorState onRetry={() => claimsQuery.refetch()} />
        ) : (
          <>
            {approved.length > 0 ? (
              <section className="flex flex-col gap-3">
                <h2 className="eyebrow text-ink-muted">
                  {copy.sectionYourProperties}
                </h2>
                {approved.map((p) => (
                  <PropertyCard key={p.claim_id} claim={p} />
                ))}
              </section>
            ) : null}

            {pending.length > 0 ? (
              <section
                className={cn(
                  "flex flex-col gap-3",
                  approved.length > 0 && "mt-6",
                )}
              >
                <h2 className="eyebrow text-ink-muted">
                  {copy.sectionPending}
                </h2>
                {pending.map((c) => (
                  <PendingClaimCard
                    key={c.claim_id}
                    claim={c}
                    onCancel={() => cancelMutation.mutate(c.claim_id)}
                    cancelling={cancelMutation.isPending}
                  />
                ))}
              </section>
            ) : null}

            {rejected.length > 0 ? (
              <section
                className={cn(
                  "flex flex-col gap-3",
                  (approved.length > 0 || pending.length > 0) && "mt-6",
                )}
              >
                <h2 className="eyebrow text-ink-muted">
                  {copy.sectionNotApproved}
                </h2>
                {rejected.map((c) => (
                  <Card key={c.claim_id} noGrain className="p-4 opacity-90">
                    <ClaimRow claim={c} />
                    {c.rejection_reason ? (
                      <p className="mt-2 text-body-sm text-danger">
                        {c.rejection_reason}
                      </p>
                    ) : null}
                  </Card>
                ))}
              </section>
            ) : null}

            {approved.length === 0 &&
            pending.length === 0 &&
            rejected.length === 0 ? (
              <EmptyState
                className="mt-4"
                icon={<HomeIcon className="h-7 w-7" strokeWidth={1.5} />}
                title="No properties yet."
                description="Claim your home so the estate can link your account to the right unit."
                action={
                  <Button onClick={() => setClaimOpen(true)}>
                    <Plus className="h-4 w-4" strokeWidth={1.5} />
                    Claim a property
                  </Button>
                }
              />
            ) : (
              <Button
                variant="secondary"
                width="full"
                className="mt-8"
                onClick={() => setClaimOpen(true)}
              >
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                Claim another property
              </Button>
            )}
          </>
        )}
      </div>

      <Fab draggable onClick={() => setClaimOpen(true)} label="Claim a property">
        <Plus className="h-5 w-5" strokeWidth={2.2} />
      </Fab>

      <ClaimPropertySheet open={claimOpen} onOpenChange={setClaimOpen} />
    </div>
  );
}

function PropertyCard({ claim }: { claim: PropertyClaim }): JSX.Element {
  return (
    <Card noGrain className="p-4">
      <ClaimRow claim={claim} />
    </Card>
  );
}

function PendingClaimCard({
  claim,
  onCancel,
  cancelling,
}: {
  claim: PropertyClaim;
  onCancel: () => void;
  cancelling: boolean;
}): JSX.Element {
  return (
    <Card noGrain className="p-4">
      <ClaimRow
        claim={claim}
        icon={<Clock className="h-5 w-5 text-warning" strokeWidth={1.5} />}
      />
      <Button
        variant="ghost"
        size="sm"
        className="mt-3"
        loading={cancelling}
        onClick={onCancel}
      >
        Withdraw claim
      </Button>
    </Card>
  );
}

function ClaimRow({
  claim,
  icon,
}: {
  claim: {
    unit_label: string;
    address?: string;
    type_name?: string;
    relationship_label?: string;
    property_residency_kind?: string;
    status: string;
    submitted_at?: string;
  };
  icon?: ReactNode;
}): JSX.Element {
  const statusTone =
    STATUS_TONE[claim.status] ?? "bg-sunken text-ink-secondary";
  return (
    <div className="flex items-start gap-3">
      {icon ?? (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-sunken text-ink-muted">
          <HomeIcon className="h-5 w-5" strokeWidth={1.25} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className="font-display text-headline leading-tight text-ink">
            {claim.unit_label}
          </p>
          <span
            className={cn(
              "shrink-0 rounded-pill px-2 py-0.5 text-[0.625rem] font-semibold uppercase",
              statusTone,
            )}
          >
            {claim.status}
          </span>
        </div>
        {claim.relationship_label ? (
          <p className="text-caption text-ink-muted">
            {claim.relationship_label}
          </p>
        ) : null}
        {claim.property_residency_kind ? (
          <p className="text-caption text-ink-muted">
            {RESIDENCY_LABEL[claim.property_residency_kind] ??
              claim.property_residency_kind}
          </p>
        ) : null}
        {claim.address ? (
          <p className="mt-1 flex items-center gap-1 text-body-sm text-ink-secondary">
            <MapPin className="h-3 w-3 shrink-0" strokeWidth={1.5} />
            <span className="line-clamp-2">{claim.address}</span>
          </p>
        ) : null}
        {claim.submitted_at && claim.status === "pending" ? (
          <p className="mt-1 text-caption text-ink-muted">
            Submitted {formatDate(claim.submitted_at)}
          </p>
        ) : null}
      </div>
    </div>
  );
}
